/**
 * B621 — the fleet's ONE clock. Every surface that shows a time of day
 * calls `clockWords`; the register (the shape of "9:45 PM" / "21:45")
 * lives here so a tenant's 12h / 24h choice is honored in exactly one
 * place, and a grep wall can keep it that way. The tenant's `timeZone`
 * is the clock's zone everywhere — never the viewer's.
 *
 * RULING 2026-10-01 (Pathik, on the Phoenix showtime chips "19:00 / 21:45"):
 * "this should be AM/PM by default - tenant can change it (like in europe)".
 * So 12h is the default; a tenant sets `ops.config.timeFormat = "24h"` to
 * change it, and that fact rides every surface's read.
 */

export type TimeFormat = "12h" | "24h";

export interface ClockOptions {
  /** The TENANT's zone — the clock's zone on every surface, never the viewer's. */
  readonly timeZone?: string;
  /** Owns only the localized words (none here); the SHAPE is fixed by the register. */
  readonly locale?: string;
  /** Absent = 12h (the ruling's default). */
  readonly timeFormat?: TimeFormat;
}

const partOf = (parts: ReadonlyArray<Intl.DateTimeFormatPart>, type: string): string =>
  parts.find((p) => p.type === type)?.value ?? "";

/**
 * "9:45 PM" / "7:00 PM" / "12:00 AM" (12h, the default — no leading zero
 * on the hour, minutes never dropped, one space before an uppercase AM/PM)
 * or "21:45" / "07:00" / "00:00" (24h). Composed from parts so that
 * neither the viewer's locale nor its habit can reshape the frame's clock.
 */
export function clockWords(ms: number, opts: ClockOptions = {}): string {
  const tz = opts.timeZone !== undefined ? { timeZone: opts.timeZone } : {};
  if ((opts.timeFormat ?? "12h") === "24h") {
    const p = new Intl.DateTimeFormat(opts.locale ?? "en-GB", {
      ...tz,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(ms);
    return `${partOf(p, "hour")}:${partOf(p, "minute")}`;
  }
  const p = new Intl.DateTimeFormat("en-US", {
    ...tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(ms);
  return `${partOf(p, "hour")}:${partOf(p, "minute")} ${partOf(p, "dayPeriod")}`;
}

/**
 * "12-hour (9:45 PM)" / "24-hour (21:45)" — the Settings toggle's live
 * example line. A fixed sample (21:45) rendered in UTC, so the words are
 * stable wherever the toggle is drawn.
 */
export function clockFormatExample(timeFormat: TimeFormat): string {
  const sample = Date.UTC(2026, 0, 1, 21, 45);
  const shown = clockWords(sample, { timeZone: "UTC", timeFormat });
  return timeFormat === "12h" ? `12-hour (${shown})` : `24-hour (${shown})`;
}
